import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../api';
import logoMark from '../assets/images/logo.jpg';
import { ActionButton } from '../ui';
import ApplicationCard from '../applications/ApplicationCard';
import useJobApplication from '../applications/useJobApplication';

/* The public job page — the one that arrives as a pasted or shared link.
   Applying happens here. It used to navigate the candidate to
   /student/dashboard instead, which dropped the job and created nothing. */

function readSession() {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const user = readSession();
  const token = localStorage.getItem('token');
  const isStudent = !!token && user?.role === 'student';

  const {
    application, hasApplied, justSubmitted, applying, notice, dismissNotice, apply,
  } = useJobApplication(id, user, token);

  useEffect(() => {
    axios.get(`${API_URL}/api/jobs/public/${id}`)
      .then(res => setJob(res.data))
      .catch(err => {
        if (err.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [id]);

  /* Signing in has to come back here. Without the return path the candidate
     lands on the dashboard and has to find the job again by hand — which is
     how a shared link used to end in no application at all. */
  const signInAndReturn = () => {
    const back = `${location.pathname}${location.search}`;
    navigate(`/login?next=${encodeURIComponent(back)}`);
  };

  const handleApply = () => {
    if (!user) return signInAndReturn();
    if (isStudent) { apply(); return; }
    // A recruiter or admin session cannot apply. Send them to their own
    // dashboard rather than silently doing nothing.
    navigate(user.role === 'recruiter' ? '/recruiter/dashboard' : '/admin/dashboard');
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--background)' }}>
        <div className="spinner" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'var(--background)', gap: 16 }}>
        <div style={{ fontSize: 48 }}>🔍</div>
        <h2 style={{ margin: 0 }}>Job not found</h2>
        <p style={{ color: 'var(--text-muted)', margin: 0 }}>This job may have been closed or the link is invalid.</p>
        <button className="btn btn-primary" onClick={() => navigate('/')}>Go to Homepage</button>
      </div>
    );
  }

  const company = job.posted_by?.company_name || job.posted_by?.name || 'Company';
  const stipend = job.stipend?.min || job.stipend?.max
    ? `₹${(job.stipend.min || 0).toLocaleString()} – ₹${(job.stipend.max || 0).toLocaleString()} / month`
    : null;
  const deadline = job.application_deadline
    ? new Date(job.application_deadline).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;
  const startDate = job.start_date
    ? new Date(job.start_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  const applyLabel = user
    ? (isStudent ? 'Apply Now' : 'Go to Dashboard')
    : 'Sign In to Apply';

  const applyButton = (props = {}) => (
    <ActionButton
      size={props.size || 'sm'}
      state={hasApplied ? 'done' : applying ? 'pending' : 'idle'}
      labels={{ idle: applyLabel, pending: 'Submitting…', done: 'Applied' }}
      onClick={handleApply}
      style={props.style}
    />
  );

  return (
    <div style={{ minHeight: '100vh', background: 'var(--background)' }}>
      {/* Nav */}
      <nav style={{
        background: 'var(--surface)', borderBottom: '1px solid var(--border)',
        padding: '0 24px', height: 60, display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 100
      }}>
        <span
          style={{ display: 'inline-flex', alignItems: 'center', gap: 10, fontWeight: 700, fontSize: 18, color: 'var(--primary)', cursor: 'pointer', letterSpacing: '-0.5px' }}
          onClick={() => navigate('/')}
        >
          <img src={logoMark} alt="" className="brand-mark" />
          HYRSKILL
        </span>
        {user ? (
          <button className="btn btn-secondary btn-sm" onClick={() => {
            if (user.role === 'student') navigate('/student/dashboard');
            else if (user.role === 'recruiter') navigate('/recruiter/dashboard');
            else navigate('/admin/dashboard');
          }}>
            Go to Dashboard
          </button>
        ) : (
          <button className="btn btn-primary btn-sm" onClick={signInAndReturn}>Sign In</button>
        )}
      </nav>

      <div style={{ maxWidth: 760, margin: '0 auto', padding: '32px 20px 64px' }}>

        {/* ── The application, once there is one ──
            Shown in place, on the job the candidate applied to, with the
            reference they can quote. The same card the Submitted Applications
            list draws, so the two always agree. */}
        {application && (
          <div className="card ui-view" style={{ marginBottom: 20, borderColor: 'var(--primary)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <span style={{ fontSize: 18 }}>{justSubmitted ? '✅' : '📄'}</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>
                  {justSubmitted ? 'Application submitted' : 'You have already applied'}
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                  Track it any time under Applications in your dashboard.
                </div>
              </div>
            </div>
            <ApplicationCard application={application} />
            <button
              className="btn btn-secondary btn-sm"
              style={{ marginTop: 14 }}
              onClick={() => navigate('/student/dashboard?tab=applications')}
            >
              View my applications →
            </button>
          </div>
        )}

        {/* ── Why an apply did not go through ──
            Every route out of here stays inside the student portal. */}
        {notice && (
          <div className={`alert alert-${notice.tone === 'warning' ? 'warning' : 'error'}`} style={{ marginBottom: 20 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>{notice.title}</div>
            <div style={{ fontSize: 13 }}>{notice.message}</div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              {notice.to && (
                <button className="btn btn-sm" onClick={() => navigate(notice.to)}>{notice.action}</button>
              )}
              <button className="btn btn-secondary btn-sm" onClick={dismissNotice}>Dismiss</button>
            </div>
          </div>
        )}

        {/* Header card */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1 style={{ margin: '0 0 8px', fontSize: 26, lineHeight: 1.3 }}>{job.title}</h1>
              <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 12 }}>{company}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <Tag>{job.position_type}</Tag>
                <Tag>{job.work_mode}</Tag>
                {job.location && <Tag>{job.location}</Tag>}
                <Tag>{job.role_category}</Tag>
              </div>
            </div>
            <div style={{ flexShrink: 0, alignSelf: 'flex-start' }}>{applyButton()}</div>
          </div>
        </div>

        {/* Key details */}
        <div className="card" style={{ marginBottom: 20 }}>
          <SectionTitle>Key Details</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px 24px' }}>
            {stipend && <Detail label="Stipend" value={stipend} />}
            {job.duration_months && <Detail label="Duration" value={`${job.duration_months} month${job.duration_months > 1 ? 's' : ''}`} />}
            {startDate && <Detail label="Start Date" value={startDate} />}
            {deadline && <Detail label="Apply By" value={deadline} />}
            {job.num_openings && <Detail label="Openings" value={job.num_openings} />}
            {job.experience && <Detail label="Experience" value={job.experience} />}
          </div>
        </div>

        {/* Description */}
        <div className="card" style={{ marginBottom: 20 }}>
          <SectionTitle>About the Role</SectionTitle>
          <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>
            {job.description}
          </p>
        </div>

        {/* Responsibilities */}
        {job.responsibilities?.length > 0 && (
          <div className="card" style={{ marginBottom: 20 }}>
            <SectionTitle>Responsibilities</SectionTitle>
            <ul style={{ margin: 0, paddingLeft: 20, color: 'var(--text-secondary)', fontSize: 15, lineHeight: 2 }}>
              {job.responsibilities.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          </div>
        )}

        {/* Skills */}
        {job.skills?.length > 0 && (
          <div className="card" style={{ marginBottom: 20 }}>
            <SectionTitle>Skills Required</SectionTitle>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {job.skills.map((s, i) => (
                <span key={i} style={{
                  background: 'var(--primary-light)', color: 'var(--primary)',
                  fontSize: 13, fontWeight: 500, padding: '5px 14px', borderRadius: 20
                }}>{s}</span>
              ))}
            </div>
          </div>
        )}

        {/* Interview steps */}
        {job.interview_steps?.length > 0 && (
          <div className="card" style={{ marginBottom: 20 }}>
            <SectionTitle>Hiring Process</SectionTitle>
            <ol style={{ margin: 0, paddingLeft: 20, color: 'var(--text-secondary)', fontSize: 15, lineHeight: 2 }}>
              {job.interview_steps.map((s, i) => <li key={i}>{s}</li>)}
            </ol>
          </div>
        )}

        {/* CTA */}
        <div style={{ textAlign: 'center', paddingTop: 8 }}>
          {applyButton({ size: 'md', style: { padding: '12px 40px', fontSize: 16 } })}
          {!user && (
            <p style={{ marginTop: 12, fontSize: 13, color: 'var(--text-muted)' }}>
              Create a free student account to apply for this opportunity.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--text-muted)', marginBottom: 14 }}>
      {children}
    </div>
  );
}

function Tag({ children }) {
  return (
    <span style={{
      background: 'var(--background)', border: '1px solid var(--border)',
      fontSize: 13, fontWeight: 500, padding: '4px 12px', borderRadius: 20,
      color: 'var(--text-secondary)'
    }}>{children}</span>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: 'var(--text-muted)', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{value}</div>
    </div>
  );
}

export default JobDetail;
